import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { localizedText } from '../i18n/contentLocales';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { listChallenges } from '../api/content';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/useAuth';
import type { Challenge, MultilingualString } from '../api/types';
import ErrorAlert from './ErrorAlert';
import LoadingSpinner from './LoadingSpinner';

interface ImportChallengeDialogProps {
  open: boolean;
  /** Challenge ids already linked to the station — shown as disabled. */
  linkedChallengeIds: string[];
  /**
   * Library station the challenge will be matched to. Challenges already
   * used on this station in other rallyes sort first (issue #254).
   */
  stationId?: string;
  onCancel: () => void;
  onImport: (challengeId: string) => Promise<void> | void;
}

const ImportChallengeDialog: React.FC<ImportChallengeDialogProps> = ({
  open,
  linkedChallengeIds,
  stationId,
  onCancel,
  onImport,
}) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation('content');
  const orgSlug = user?.organization_slug;

  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [selected, setSelected] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const activeLang = i18n.language.startsWith('de') ? 'de' : 'en';
  const titleOf = (ml: MultilingualString) => localizedText(ml, activeLang);

  const load = useCallback(async () => {
    if (!orgSlug) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await listChallenges(orgSlug, { limit: 200 });
      setChallenges(resp.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgSlug]);

  useEffect(() => {
    if (open) {
      setSearch('');
      setSelected(null);
      void load();
    }
  }, [open, load]);

  const linked = useMemo(() => new Set(linkedChallengeIds), [linkedChallengeIds]);

  // Station-relevant first (already matched to this library station in other
  // rallyes), then most recently updated — with text search + type filter on
  // top (issue #254).
  const isRelevant = (c: Challenge) =>
    !!stationId && (c.usage?.station_ids ?? []).includes(stationId);

  const visible = useMemo(
    () =>
      challenges
        .filter((c) => {
          if (typeFilter !== 'all' && c.challenge_type !== typeFilter) return false;
          if (!search) return true;
          return titleOf(c.title).toLowerCase().includes(search.toLowerCase());
        })
        .sort((a, b) => {
          const rel = Number(isRelevant(b)) - Number(isRelevant(a));
          if (rel !== 0) return rel;
          return b.updated_at.localeCompare(a.updated_at);
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [challenges, search, typeFilter, stationId, activeLang],
  );

  const handleImport = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      await onImport(selected);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{t('challenge.import.title')}</DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2, mt: 1 }}>
          <TextField
            label={t('challenge.import.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            select
            label={t('challenge.import.typeFilter')}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">{t('challenge.import.allTypes')}</MenuItem>
            {(['photo', 'text', 'qr_scan', 'quiz', 'creative'] as const).map((ct) => (
              <MenuItem key={ct} value={ct}>
                {t(`challenge.types.${ct}`)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        {error && <ErrorAlert message={error} />}
        {loading ? (
          <LoadingSpinner />
        ) : visible.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
            {t('challenge.import.empty')}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" aria-label={t('challenge.import.title')}>
              <TableHead>
                <TableRow>
                  <TableCell scope="col">{t('challenge.list.table.name')}</TableCell>
                  <TableCell scope="col">{t('challenge.list.table.type')}</TableCell>
                  <TableCell scope="col">{t('challenge.list.table.usage')}</TableCell>
                  <TableCell scope="col" align="right">
                    {t('challenge.list.table.points')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((c) => {
                  const isLinked = linked.has(c.id);
                  const stationTitles = (c.usage?.station_titles ?? [])
                    .map((st) => titleOf(st))
                    .filter(Boolean);
                  const maxPoints =
                    typeof c.scoring_rules?.max_points === 'number'
                      ? c.scoring_rules.max_points
                      : 0;
                  return (
                    <TableRow
                      key={c.id}
                      hover={!isLinked}
                      selected={selected === c.id}
                      onClick={() => !isLinked && setSelected(c.id)}
                      sx={{
                        cursor: isLinked ? 'default' : 'pointer',
                        opacity: isLinked ? 0.5 : 1,
                      }}
                      aria-disabled={isLinked}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>
                        {titleOf(c.title) || c.id}
                        {!isLinked && isRelevant(c) && (
                          <Chip
                            label={t('challenge.import.relevant')}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                        {isLinked && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({t('challenge.import.alreadyLinked')})
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={t(`challenge.types.${c.challenge_type}`)} size="small" />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {stationTitles.length > 0
                          ? stationTitles.join(', ')
                          : t('challenge.list.table.unused')}
                      </TableCell>
                      <TableCell align="right">{maxPoints}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          {t('actions.cancel', { ns: 'common' })}
        </Button>
        <Button variant="contained" onClick={handleImport} disabled={!selected || submitting}>
          {t('challenge.import.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportChallengeDialog;
